import React, { useEffect, useMemo, useState } from 'react';
import { ImageIcon, Loader2, Upload, X } from 'lucide-react';
import {
  fieldClass,
  labelClass,
  primaryBtnClass,
  secondaryBtnClass,
} from '../styles';
import { fetchBlobs, uploadBlob } from './api';
import type { BlobItem } from './types';

interface BlobPickerProps {
  value: string;
  imageUrl?: string;
  configured: boolean;
  container?: string | null;
  onChange: (name: string, url?: string) => void;
  label?: string;
}

type ModalTab = 'browse' | 'upload';

const BlobPicker: React.FC<BlobPickerProps> = ({
  value,
  imageUrl,
  configured,
  container,
  onChange,
  label = 'Image',
}) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ModalTab>('browse');
  const [blobs, setBlobs] = useState<BlobItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [uploading, setUploading] = useState(false);

  const previewUrl = imageUrl || undefined;

  useEffect(() => {
    if (!open || !configured) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchBlobs(search.trim() || undefined);
        if (cancelled) return;
        if (!data.configured) {
          setError(data.detail || 'Blob storage is not configured.');
          setBlobs([]);
        } else {
          setBlobs(data.blobs || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to list blobs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, configured, search]);

  const imageBlobs = useMemo(
    () =>
      blobs.filter((b) => {
        const ct = (b.content_type || '').toLowerCase();
        if (ct.startsWith('image/')) return true;
        return /\.(png|jpe?g|gif|webp|svg)$/i.test(b.name);
      }),
    [blobs]
  );

  const handleUpload = async () => {
    if (!uploadFile) {
      setError('Choose a file to upload.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const name = uploadName.trim() || uploadFile.name;
      const result = await uploadBlob(uploadFile, name, overwrite);
      onChange(result.name, result.url);
      setOpen(false);
      setUploadFile(null);
      setUploadName('');
      setOverwrite(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-28 h-28 rounded-md border border-[#E8E8E8] bg-[#FAFAFA] flex items-center justify-center overflow-hidden shrink-0">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-7 h-7 text-[#C0C0C0]" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            className={fieldClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Blob filename (e.g. category-workshops.png)"
          />
          {!configured ? (
            <p className="text-[12px] text-[#8A8A8A]">
              Blob storage is not configured. Enter a filename manually
              {container ? ` for container "${container}"` : ''}.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={secondaryBtnClass}
                onClick={() => {
                  setTab('browse');
                  setOpen(true);
                  setError(null);
                }}
              >
                Browse
              </button>
              <button
                type="button"
                className={`${secondaryBtnClass} inline-flex items-center gap-1`}
                onClick={() => {
                  setTab('upload');
                  setOpen(true);
                  setError(null);
                }}
              >
                <Upload className="w-3.5 h-3.5" />
                Upload
              </button>
              {value && (
                <button
                  type="button"
                  className={secondaryBtnClass}
                  onClick={() => onChange('')}
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {open && configured && (
        <div
          className="fixed inset-0 z-[16000] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#E8E8E8] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-[#0C0B0B]">Marketplace visuals</h3>
                {container && (
                  <p className="text-[11px] text-[#8A8A8A] mt-0.5">Container: {container}</p>
                )}
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close">
                <X className="w-5 h-5 text-[#626262]" />
              </button>
            </div>
            <div className="px-4 pt-3 flex gap-2">
              <button
                type="button"
                className={`h-8 px-3 rounded-full text-[13px] font-medium ${
                  tab === 'browse' ? 'bg-[#0C0B0B] text-white' : 'bg-[#F3F3F3] text-[#4A4A4A]'
                }`}
                onClick={() => setTab('browse')}
              >
                Browse
              </button>
              <button
                type="button"
                className={`h-8 px-3 rounded-full text-[13px] font-medium ${
                  tab === 'upload' ? 'bg-[#0C0B0B] text-white' : 'bg-[#F3F3F3] text-[#4A4A4A]'
                }`}
                onClick={() => setTab('upload')}
              >
                Upload
              </button>
            </div>

            {error && (
              <div className="mx-4 mt-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">
                {error}
              </div>
            )}

            <div className="p-4 flex-1 overflow-y-auto">
              {tab === 'browse' && (
                <div className="space-y-3">
                  <input
                    className={fieldClass}
                    placeholder="Filter by filename…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {loading ? (
                    <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading blobs…
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {imageBlobs.map((blob) => (
                        <button
                          key={blob.name}
                          type="button"
                          onClick={() => {
                            onChange(blob.name, blob.url);
                            setOpen(false);
                          }}
                          className={`text-left border rounded-lg overflow-hidden hover:border-[#3da3d4] ${
                            value === blob.name ? 'border-[#3da3d4] ring-1 ring-[#3da3d4]' : 'border-[#E8E8E8]'
                          }`}
                        >
                          <div className="aspect-square bg-[#FAFAFA] flex items-center justify-center">
                            {blob.url ? (
                              <img
                                src={blob.url}
                                alt={blob.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <ImageIcon className="w-6 h-6 text-[#C0C0C0]" />
                            )}
                          </div>
                          <div className="px-2 py-1.5">
                            <div className="text-[11px] text-[#0C0B0B] truncate" title={blob.name}>
                              {blob.name}
                            </div>
                            <div className="text-[10px] text-[#8A8A8A]">
                              {blob.size != null ? `${Math.round(blob.size / 1024)} KB` : '—'}
                            </div>
                          </div>
                        </button>
                      ))}
                      {imageBlobs.length === 0 && (
                        <div className="col-span-full text-sm text-[#8A8A8A] py-6 text-center">
                          No images found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tab === 'upload' && (
                <div className="space-y-3 max-w-md">
                  <div>
                    <label className={labelClass}>File</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setUploadFile(file);
                        if (file && !uploadName) setUploadName(file.name);
                      }}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Blob name</label>
                    <input
                      className={fieldClass}
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      placeholder="optional rename"
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A]">
                    <input
                      type="checkbox"
                      checked={overwrite}
                      onChange={(e) => setOverwrite(e.target.checked)}
                    />
                    Overwrite if exists
                  </label>
                  <button
                    type="button"
                    disabled={uploading || !uploadFile}
                    onClick={() => void handleUpload()}
                    className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
                  >
                    {uploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    Upload and select
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlobPicker;
